import clsx from "classnames";
import { useEffect, useRef, useState } from "react";
import { Container } from "reactstrap";
import Buttons from "ui/Buttons/component";
import styles from "./styles.module.scss";
import BrushIcon from "/public/site-assets/svg/brush.svg";
import ClipIcon from "/public/site-assets/svg/clip.svg";
import LeafIcon from "/public/site-assets/svg/leaf.svg";
import LightBlulbIcon from "/public/site-assets/svg/light-blub.svg";
import PencilIcon from "/public/site-assets/svg/pencil.svg";

import BlogCard from "@/components/Shared/BlogCard";
import FooterWave2Icon from "@/public/site-assets/svg/footer-wave-2.svg";
import Arrow from "@/public/site-assets/svg/swiper-arrow.svg";
import { motion } from "framer-motion";

import useBlogs from "hooks/useBlogs";
import { Autoplay, Navigation } from "swiper";
import "swiper/css";
import "swiper/css/autoplay";
import { Swiper, SwiperSlide } from "swiper/react";

export const LatestBlogs = ({
  className,
  heading,
  swiperID = "latest-blogs-swiper",
  xl,
  lg,
  md,
  sm,
  buttons,
  swiperLoop = true,
  alignHeading = "start",
  showWave,
  waveDirection,
  waveColor,
  waveBackground,
  showTopWave,
  topWaveDirection,
  topWaveColor,
  topWaveBackground,
}) => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const [displayCount, setDisplayCount] = useState(5);

  const settings = {
    modules: [Navigation, Autoplay],
    loop: swiperLoop,
    spaceBetween: 50,
    centeredSlides: false,
    slidesPerView: 1,
    autoHeight: false,
    speed: 500,
    effect: "fade",
    fadeEffect: { crossFade: true },
    autoplay: {
      delay: 5000,
    },
    navigation: {
      nextEl: `.${swiperID}.swiper-next`,
      prevEl: `.${swiperID}.swiper-prev`,
    },
    breakpoints: {
      320: {
        spaceBetween: 10,
        slidesPerView: sm,
      },
      768: {
        spaceBetween: 50,
        slidesPerView: md,
      },
      991: {
        spaceBetween: 50,
        slidesPerView: lg,
      },
      1399: {
        spaceBetween: 50,
        slidesPerView: xl,
      },
    },
  };

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.1, // Adjust threshold as needed, make it smaller for more sensitive detection
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    }, options);

    observer.observe(sectionRef.current);

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobileDevice = window.innerWidth <= 991; // Adjust threshold for mobile devices
      // setIsVisible(!mobileDevice); // Hide motion on mobile devices
    };

    handleResize(); // Call initially
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const post = useBlogs();

  return (
    <>
      {showTopWave && (
        <div
          className={clsx(
            "footer",
            topWaveDirection === "down" && "rotate",
            topWaveColor,
            topWaveBackground,
          )}
          style={{ marginBottom: "-1px", zIndex: 1, position: "relative" }}
        >
          <FooterWave2Icon />
        </div>
      )}
      <section
        ref={sectionRef}
        className={clsx(
          className,
          styles.root,
          showTopWave ? "pt-0" : "pt-5",
          showWave ? "pb-0" : "pb-5",
        )}
      >
        <Container className="position-relative">
          <div className="floating-icon">
            <LightBlulbIcon />
          </div>
          <div className="floating-icon">
            <LeafIcon />
          </div>
          <div className="floating-icon">
            <PencilIcon />
          </div>
          <div className="floating-icon">
            <ClipIcon />
          </div>
          <div className="floating-icon">
            <BrushIcon />
          </div>

          <div
            className={`d-flex justify-content-between gap-4 align-items-${alignHeading} mb-4 wrapper`}
          >
            {heading && <h4 className={`h3`}>{heading}</h4>}

            <div className="d-flex align-items-center">
              <button
                className={`${swiperID} swiper-prev border-0 bg-transparent`}
                aria-label="Prev"
              >
                <Arrow />
              </button>
              <button
                className={`${swiperID} swiper-next border-0 bg-transparent`}
                aria-label="Next"
              >
                <Arrow />
              </button>
            </div>
          </div>

          <div className="swiper-container">
            <Swiper {...settings}>
              {post.slice(0, displayCount).map((postItem, index) => (
                <SwiperSlide key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={isVisible ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <BlogCard post={postItem} />
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          {buttons && (
            <Buttons buttons={buttons} className="d-flex align-items-center text-center" />
          )}
        </Container>
      </section>
      {showWave && (
        <div
          className={clsx(
            "footer",
            waveDirection === "down" && "rotate",
            waveColor,
            waveBackground,
          )}
          style={{ marginTop: "-1px" }}
        >
          <FooterWave2Icon />
        </div>
      )}
    </>
  );
};

export default LatestBlogs;
